export function savedRuntimeKey(novelId: string): string {
  return `lorevok:${novelId}:runtime`;
}

// localStorage can throw (private mode, disabled storage, quota exceeded).
// Saved scene progress is a convenience, not core state, so swallow failures.
export function safeLocalStorageGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

export function safeLocalStorageSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // Ignore: progress persistence is best-effort.
  }
}

export function safeLocalStorageRemove(key: string): void {
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore: storage may be unavailable.
  }
}

// Which novels this browser has finished (reached an ending on). Without real
// auth this is the per-browser "already played it" signal, used to pick a fresh
// suggestion when a generation starts.
const FINISHED_NOVELS_KEY = "lorevok:finishedNovels";

function readFinishedNovels(): Set<string> {
  const raw = safeLocalStorageGet(FINISHED_NOVELS_KEY);
  if (!raw) {
    return new Set();
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed)
      ? new Set(parsed.filter((id): id is string => typeof id === "string"))
      : new Set();
  } catch {
    return new Set();
  }
}

export function isNovelFinished(id: string): boolean {
  return readFinishedNovels().has(id);
}

/** Records that this browser reached an ending of `id`. Idempotent. */
export function markNovelFinished(id: string): void {
  const ids = readFinishedNovels();
  if (ids.has(id)) {
    return;
  }
  ids.add(id);
  safeLocalStorageSet(FINISHED_NOVELS_KEY, JSON.stringify([...ids]));
}

// Tracks the id of an in-progress generation job so the status page can be
// restored after a reload. Generation itself runs server-side, so closing the
// tab does not stop it — this only re-attaches the UI to the running job.
export const ACTIVE_JOB_KEY = "lorevok:activeJob";
